import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Avatar, Box, Button, IconButton, Popover, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMappedTranslation, useUser } from 'hooks'
import { EllipsisStyle } from 'styles/theme/theme'
import { isCJK, isFamilyNameOrderFirst } from 'utils/i18n'

import LogoIcon from './assets/images/logo-samsung.svg'
import IconPerson from './assets/images/person.svg'
import Menu from './menu'
import { AutomationTagIds } from '../../../assets/test/test-automation-tag-id'

export function HeaderWithMenu() {
  const navigate = useNavigate()
  const mappedTranslation = useMappedTranslation()
  const theme = useTheme()

  const { user } = useUser()

  const photoUrl = user.photoUrl

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const isOpen = Boolean(anchorEl)

  const SAMSUNG_URL = 'https://www.samsung.com'

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSignOut = () => {
    handleClose()
    navigate('/dashboard/logout', { state: true })
  }

  // TODO : Need breakpoint integration with samsung.com
  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          bgcolor: theme.palette.mode === 'light' ? '#ffffff' : null
        }}
      >
        <Box
          sx={{
            height: {
              xs: '56px',
              md: '80px'
            },
            display: 'flex',
            maxWidth: '1440px',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '16px',
            margin: '0 auto'
          }}
        >
          <IconButton
            disableRipple
            sx={{ padding: 0, height: '32px', minWidth: '120px' }}
            onClick={() => {
              window.location.href = SAMSUNG_URL
            }}
            data-testid='IconButtonC5249E9E'
          >
            <Box
              component='img'
              alt=''
              src={LogoIcon}
              sx={{
                display: 'block',
                height: {
                  xs: '14px',
                  md: '16px'
                },
                filter: theme.palette.mode === 'dark' ? 'invert(1)' : null
              }}
            />
          </IconButton>

          <IconButton
            disableRipple
            onClick={handleOpen}
            sx={{ padding: 0 }}
            aria-label={mappedTranslation('iam.Accessibility.Button.Attribute.open.account.menu.to.sign.out')}
            data-testid='IconButtonBE40555E'
          >
            {photoUrl ? (
              <Avatar src={photoUrl} sx={{ height: '36px', width: '36px' }} />
            ) : (
              <Box
                component='img'
                alt=''
                src={IconPerson}
                sx={{ height: '36px', width: '36px', filter: theme.palette.mode === 'dark' ? 'invert(1)' : null }}
                data-testid={AutomationTagIds['button.person.icon']}
              />
            )}
          </IconButton>
          <Popover
            open={isOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            marginThreshold={12}
            sx={{
              marginTop: {
                xs: '10px',
                md: '22px'
              },
              '& .MuiPopover-paper': {
                maxWidth: { xs: '336px', md: '348px' },
                width: { xs: '336px', md: '348px' },
                border: 0,
                padding: { xs: '30px 24px 0', md: '40px 40px 0' }
              }
            }}
            onClose={handleClose}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}
                data-testid={AutomationTagIds['text.user.detail']}
              >
                {photoUrl ? (
                  <Avatar src={photoUrl} sx={{ height: '48px', width: '48px' }} />
                ) : (
                  <Box
                    component='img'
                    alt=''
                    src={IconPerson}
                    sx={{
                      height: '48px',
                      width: '48px',
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : null
                    }}
                  />
                )}
                <Box
                  sx={{
                    height: '48px',
                    marginLeft: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    ...EllipsisStyle,
                    flex: '1',
                    overflow: 'hidden'
                  }}
                >
                  <Typography sx={{ fontSize: '18px', lineHeight: '24px', fontWeight: 500 }}>
                    {isCJK(user.firstName + user.lastName)
                      ? `${user.lastName}${user.firstName}`
                      : isFamilyNameOrderFirst()
                      ? `${user.lastName} ${user.firstName}`
                      : `${user.firstName} ${user.lastName}`}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: 'auto',
                  marginY: '30px',
                  width: { xs: '216px', md: '236px' }
                }}
              >
                <Button
                  disableElevation
                  variant='contained'
                  color='secondary'
                  onClick={handleSignOut}
                  fullWidth={true}
                  data-testid={AutomationTagIds['button.signout']}
                >
                  {mappedTranslation('common.button.signOut')}
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>
      </Box>
      <Menu />
    </Box>
  )
}
